// src/Api.jsx
import axios from "axios";
import dayjs from "dayjs";

// 인증 헤더가 필요한 요청에 사용하는 공유 axios 인스턴스
const createAuthAxios = (token) =>
  axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token.access}`,
    },
  });

const getKoreanFormattedDate = () => {
  return new Date()
    .toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    .replace(/\./g, "")
    .replace(/ /g, "-");
};

export const fetchPhotos = async (date, token) => {
  const formattedDate =
    typeof date === "string" ? date : date.format("YYYY-MM");

  const response = await createAuthAxios(token).post("/api/GetPhoto", {
    date: formattedDate,
  });

  return response.data;
};

export const fetchTodayPhotoLength = async (token, setTodayPhotosCount) => {
  try {
    const response = await createAuthAxios(token).post(
      "/api/GetTodayPhotoLength",
      { date: getKoreanFormattedDate() }
    );

    const data = response.data;

    // Recoil 상태를 업데이트하는 부분
    setTodayPhotosCount({
      count1: data.count > 0,
      count2: data.count2 > 0,
      count3: data.count3 > 0,
    });

    return data; // 필요한 경우 데이터 반환
  } catch (error) {
    console.error("Error fetching today photo length:", error);
    throw error; // 오류 발생 시 에러를 던져 호출자에게 전달
  }
};

export const fetchDelete = async (id, token) => {
  try {
    const response = await createAuthAxios(token).delete("/api/DeletePhoto", {
      data: { id },
    });

    return response.status; // 응답 상태 코드 반환
  } catch (error) {
    console.error("Error deleting photo:", error);
    throw error; // 오류 발생 시 에러를 던져 호출자에게 전달
  }
};

export const fetchLike = async (id, writer, token, diff) => {
  const likeApiPaths = {
    0: "/api/LikePhoto",
    1: "/api/LikePhotoCam",
    2: "/api/LikePhotoDiary",
  };
  try {
    const path = likeApiPaths[diff];
    const response = await createAuthAxios(token).patch(path, {
      id,
      writer,
    });
    return response.status;
  } catch (error) {
    console.error("Error Like photo:", error);
    throw error;
  }
};

export const fetchDeleteDiary = async (batNum, token) => {
  try {
    const response = await createAuthAxios(token).delete(
      "/api/DeletePhotoDiary",
      { data: { batNum } }
    );
    return response.status; // 응답 상태 코드 반환
  } catch (error) {
    console.error("Error deleting photo:", error);
    throw error; // 오류 발생 시 에러를 던져 호출자에게 전달
  }
};

export const fetchUpdate = async (id, memo, token) => {
  try {
    const response = await createAuthAxios(token).patch("/api/UpdatePhoto", {
      id,
      memo,
    });
    return response.status; // 응답 상태 코드 반환
  } catch (error) {
    console.error("Error deleting photo:", error);
    throw error; // 오류 발생 시 에러를 던져 호출자에게 전달
  }
};

// CamCorder
export const fetchCam = async (date, token) => {
  const formattedDate =
    typeof date === "string" ? date : date.format("YYYY-MM");

  const response = await createAuthAxios(token).post("/api/GetPhotoCam", {
    date: formattedDate,
  });

  return response.data;
};

export const fetchDeleteCam = async (id, token) => {
  try {
    const response = await createAuthAxios(token).delete(
      "/api/DeletePhotoCam",
      { data: { id } }
    );

    return response.status; // 응답 상태 코드 반환
  } catch (error) {
    console.error("Error deleting photo:", error);
    throw error; // 오류 발생 시 에러를 던져 호출자에게 전달
  }
};

export const fetchUpdateCam = async (id, title, memo, token) => {
  try {
    const response = await createAuthAxios(token).patch(
      "/api/UpdatePhotoCam",
      { id, title, memo }
    );
    return response.status; // 응답 상태 코드 반환
  } catch (error) {
    console.error("Error deleting photo:", error);
    throw error; // 오류 발생 시 에러를 던져 호출자에게 전달
  }
};

// PhotoDiary
export const fetchUpdateDiary = async (batch_number, title, diary, token, images) => {
  try {
    let response;
    if (images && images.length > 0) {
      const data = new FormData();
      data.append("batch_number", batch_number);
      data.append("title", title);
      data.append("diary", diary);
      images.forEach((img) => data.append("image", img));
      // FormData일 때는 Content-Type을 axios 인스턴스 기본값(application/json)이
      // 덮어쓰지 않도록 plain axios로 직접 호출
      response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/UpdatePhotoDiary`,
        data,
        { headers: { Authorization: `Bearer ${token.access}` } }
      );
    } else {
      response = await createAuthAxios(token).patch("/api/UpdatePhotoDiary", {
        batch_number,
        title,
        diary,
      });
    }
    return response.status; // 응답 상태 코드 반환
  } catch (error) {
    console.error("Error updating diary:", error);
    throw error; // 오류 발생 시 에러를 던져 호출자에게 전달
  }
};

export const getCurrentDateYYYYMM = () => {
  const today = dayjs();
  return today.startOf("month");
};

//PhotoDiary
export const fetchPhotoDiary = async (date, token) => {
  const formattedDate =
    typeof date === "string" ? date : date.format("YYYY-MM");

  const response = await createAuthAxios(token).post("/api/GetPhotoDiary", {
    date: formattedDate,
  });
  return response.data;
};

//로그인
export const fetchLogin = async (username, password, pushToken) => {
  try {
    // 첫 번째 요청: 로그인
    const response = await axios.post(`/api/Login`, {
      username, // 간결하게 표현
      password, // 간결하게 표현
    });
    if (pushToken !== "") {
      // 로그인 성공 후 두 번째 요청: PushToken
      await axios.post(`${process.env.REACT_APP_API_URL}/api/PushToken`, {
        username, // 로그인 응답에서 사용자 ID 추출
        pushToken, // 필요한 push token으로 교체
      });
    }

    return {
      status: response.status,
      data: response.data,
    };
  } catch (error) {
    console.error("Error Login:", error);
    throw error; // 오류 발생 시 에러를 던져 호출자에게 전달
  }
};

//UserInfo
export const fetchUserInfo = async (user_id, token) => {
  const response = await createAuthAxios(token).post("/api/GetUserInfo", {
    id: user_id,
  });
  return {
    status: response.status,
    data: response.data,
  };
};

export const fetchSaveCommentToBackend = async (comment, token) => {
  const response = await createAuthAxios(token).post("/api/PhotoComments", comment);
  return response.status;
};

export const fetchTest = async (token) => {
  const response = await createAuthAxios(token).post("/api/PhotoTest");
  return response.status;
};
